import { Component, Input, OnInit } from '@angular/core';
import { TabModel } from '@app/common/models/tab.model';
import { StateService } from '@uirouter/angular';
import { OccupationModel } from '@app/ps/models/occupation.model';
import { Restangular } from 'ngx-restangular';
import { ExpertModel } from '@app/ps/models/expert.model';
import { SwitchOption } from '@app/common/components/switch/switch.component';
import { AuthService } from '@app/common/services/auth.service';
import { BrandService } from "@app/common/services/brand.service";
import { OccupationSolutionTypeModel } from "@app/ps/models/occupation-solution-type.model";
import { ListService } from "@app/common/services/list.service";
import { FinalSolutionStateEnum } from '@app/ps/enums/final-solution-state.enum';
import { ConstructionObjectNamePipe } from '@app/common/pipes/construction-object-name.pipe';
import { OccupationTypeModel } from '@app/ps/models/occupation-type.model';
import { FinalSolutionTypesEnum } from "@app/ps/enums/final-solution-types.enum";
import { ProjectTypesEnum } from "@app/ps/enums/project-types.enum";
import { PERMANENT_WITH_PURCHASE } from '@app/ps/enums/occupation-type.enum';
import { asQueryList } from "@angular/core/src/view";

@Component({
  templateUrl: './update-occupation.component.html',
  styleUrls: ['./update-occupation.component.scss'],
})
export class UpdateOccupationComponent implements OnInit {
  @Input() titleId: number;
  @Input() parcelId: number;
  @Input() occupationId: number;
  @Input() constructionObjectId: number;

  readonly permanentWithPurchase = PERMANENT_WITH_PURCHASE;
  public occupationTypeName: string;

  occupationObject: OccupationModel;
  private originalOccupationObject: OccupationModel;
  public priceType: SwitchOption[];
  public priceUnit: SwitchOption[];
  public priorityOptions = [];
  public priorityHolder = { priorityIndex: null };
  public isInvestTypeProject: boolean;
  public isAdditionalTypeProject: boolean;
  public visibleEditOccupationSolutionType: boolean;
  public occupationSolutionTypeOptions: OccupationSolutionTypeModel[];
  public occupationTypeList: any[] = [];
  public acquirerList: any[] = [];
  public constructionObjectNamePipe: ConstructionObjectNamePipe;

  private priceAssesment;
  private originalFinalSolutionTypeId: number;
  private originalFinalSolutionReason: string;

  protected readonly FinalSolutionTypesEnum = FinalSolutionTypesEnum;

  public occupationSolutionOptions = [
    {id: null, value: 'Jiný způsob řešení'},
    {id: 'permanentWithSolutionTypeUnderOneYear', value: 'Nájem do 1 roku'},
    {id: 'permanentWithSolutionTypeOverOneYear', value: 'Nájem nad 1 rok'},
  ];

  public editModeOptions: TabModel[] = [
    { id: 'price', name: 'Určení ceny' },
    { id: 'solution-type', name: 'Způsob řešení záboru' },
    { id: 'final-solution-type', name: 'Návrh změny způsobu vypořádání' },
    { id: 'priority', name: 'Priorita záboru' },
  ];

  isActiveTab = (id: string) => id === this.editMode;

  public editMode = "";

  constructor(
    private stateService: StateService,
    private restangular: Restangular,
    private authService: AuthService,
    private brandService: BrandService,
    private listLoadService: ListService,
    public constructionObjectNamePipeService: ConstructionObjectNamePipe,
  ) {
    this.goToDetail = this.goToDetail.bind(this);
    this.update = this.update.bind(this);
    this.constructionObjectNamePipe = constructionObjectNamePipeService;
    this.priceType = [
      {
        value: 'Cenový výměr ministerstva financí',
        id: 'F',
      },
      {
        value: 'Znalecký posudek',
        id: 'E',
      },
      {
        value: 'Jiný způsob',
        id: 'O',
      },
    ];

    this.priceUnit = [
      {
        value: 'Roční cena za metr čtvereční',
        id: 'A',
      },
      {
        value: 'Jednorázová cena',
        id: 'T',
      },
    ];
  }

  async ngOnInit() {
    await this.loadOccupationSolutionTypes();
    await this.loadOccupation();
    await this.loadOccupationTypes();
    this.originalOccupationObject = { ...this.occupationObject };
    if (this.authService.getActualProject().type === ProjectTypesEnum.INVEST) {
      await this.loadAcquirers();
    }

    this.isInvestTypeProject = this.authService.getActualProject().type === ProjectTypesEnum.INVEST;
    this.isAdditionalTypeProject = this.authService.getActualProject().type === ProjectTypesEnum.ADDITIONAL;
    this.visibleEditOccupationSolutionType = this.isInvestTypeProject || (this.isAdditionalTypeProject && this.occupationObject.occupationType && this.occupationObject.occupationType.category === 'permanent');

    const applicable = this.getApplicableEditModeOptions();
    if (applicable.length > 0) {
      this.editMode = applicable[0].id;
    }
  }

  getApplicableEditModeOptions(): TabModel[] {
    if (!this.occupationObject) return [];
    const applicable: TabModel[] = [];
    if (!!(this.occupationObject.solutionType || this.occupationLongerThanOneYear() || this.occupationShorterThanOneYear())) {
      applicable.push(this.editModeOptions.find(o => o.id === 'price'));
    }
    if (this.isSolutionType()) {
      applicable.push(this.editModeOptions.find(o => o.id === 'solution-type'));
    }
    if (this.occupationSolutionTypeOptions.length > 0 && this.occupationObject.occupationType && (
      (this.isInvestTypeProject && this.occupationObject.countCases === 0) ||
      (this.isAdditionalTypeProject && this.occupationObject.occupationType.category === 'permanent') && this.occupationObject.countCasesNotOwnersStatementLiability === 0)
    ) {
      applicable.push(this.editModeOptions.find(o => o.id === 'final-solution-type'));
    }
    if (this.priorityOptions.length > 0 && this.occupationObject.occupationType.category === 'permanent') {
      applicable.push(this.editModeOptions.find(o => o.id === 'priority'));
    }
    return applicable;
  }

  showModeSelector(): boolean {
    return this.getApplicableEditModeOptions().length >= 2;
  }

  onEditModeChange(newMode: string) {
    this.occupationObject = { ...this.originalOccupationObject };
    this.editMode = newMode;
  }

  public onRentalPriceTypeChange(newValue) {
    this.occupationObject.rentalPriceType = newValue;
    this.updateAssessment();
  }

  updateAssessment() {
    if (this.priceAssesment && this.occupationObject.rentalPriceType === 'F') {
        this.occupationObject.priceAssessmentYear = this.priceAssesment.year;
        this.occupationObject.priceAssessmentPrice = this.priceAssesment.prices.find(p => p.areaId === this.occupationObject.parcel.katuzeKod);
        this.occupationObject.priceAssessmentLandPrice = this.occupationObject.priceAssessmentPrice ? this.occupationObject.priceAssessmentPrice.price : null;
    }
  }

  async loadOccupationSolutionTypes() {

    this.occupationSolutionTypeOptions = await this.listLoadService.fetchResult(this.listLoadService.createList("occupation-solution-types", {
      limit: null,
    })).then((data) => {
      if (data.list && !data.list.length) return [];
      const type = this.authService.getActualProject().type;
      let list = data.list;
      if (type ===  ProjectTypesEnum.ADDITIONAL) list = list.slice(1, 6);
      if (type ===  ProjectTypesEnum.INVEST) list = list.slice(7);
      return list || [];
    })
  }

  async loadOccupationTypes() {
    const data = await this.restangular.all('occupation-types')
      .customPOST({ filter: { filters: { validity: ['valid'] } } })
      .toPromise();
    const list: OccupationTypeModel[] = data.plain ? data.plain() : data;
    this.occupationTypeList = list.filter(t => t.number !== this.occupationObject.zabtyp);
  }

  async loadAcquirers() {
    const data = await this.restangular.all('occupations').customGET('nabyvatel-list').toPromise();
    const list: any[] = Array.isArray(data) ? data : (data.plain ? data.plain() : data);
    this.acquirerList = (list || []).map(n => ({
      id: n.nabyvatel,
      name: n.nabyvatel,
      acquirerId: n.acquirerId,
      typNabyvatele: n.typNabyvatele,
      icoNabyvatele: n.icoNabyvatele,
    }));

    const oc = this.occupationObject && this.occupationObject.finalSolutionOtherChange;
    if (oc.newNabyvatel && oc.newNabyvatel.nabyvatel && !oc.newNabyvatel.id) {
      oc.newNabyvatel = this.acquirerList.find(a => a.id === oc.newNabyvatel.nabyvatel) || oc.newNabyvatel;
    }
  }

  async loadOccupation() {
    this.occupationObject = await this.restangular.one('occupations', this.occupationId).get({loadCollections: ['rentalExpert', 'priceAssessmentPrice', 'constructionObjects']}).toPromise();
    this.occupationObject.constructionObjects = this.occupationObject.constructionObjects.filter(co => !co.endDate);

    // CR-4576 Funkce vypnuta
    /*this.priceAssesment = await this.restangular.all('price-assessments').all('list').customPOST().toPromise().then(r => {
      return r[r.length-1];
    });*/
    this.occupationObject.rentalPriceType = this.occupationObject.rentalPriceType || 'F';
    this.occupationObject.rentalPriceUnit = this.occupationObject.rentalPriceUnit || 'A';
    if (this.occupationObject.solutionType === 'permanentWithSolutionUnderOneYear') {
      this.occupationObject.rentalLength = this.occupationObject.rentalLength || 12;
    }
    this.occupationObject.solutionType = this.occupationObject.solutionType || null;
    this.occupationObject.priorityIndex = this.occupationObject.priorityIndex || 0;

    this.originalFinalSolutionTypeId = this.occupationObject.finalSolutionType ? this.occupationObject.finalSolutionType.id : null;
    this.originalFinalSolutionReason = this.occupationObject.finalSolutionReason || null;
    this.occupationObject.finalSolutionOtherChange = this.occupationObject.finalSolutionOtherChange || {};
    if (!this.occupationObject.finalSolutionOtherChange.newConstructionObjects) {
      this.occupationObject.finalSolutionOtherChange.newConstructionObjects = [];
    }
    this.occupationTypeName = this.occupationLongerThanOneYear() ? 'Dočasný zábor nad 1 rok' : (this.occupationShorterThanOneYear() ? 'Dočasný zábor do 1 roku' : this.occupationObject.zabtyptx);
    this.updateAssessment();

    const project = this.authService.getActualProject();
    if (project && project.occupationsPriority && project.occupationsPriority.enabled) {
      this.priorityOptions = project.occupationsPriority.categories
        .map((c: any, i: number) => ({ id: i, name: c.name }));
      this.priorityHolder.priorityIndex = this.priorityOptions
        .find(o => o.id === (this.occupationObject.priorityIndex ? this.occupationObject.priorityIndex : 0)) || this.priorityOptions[0];
    }
  }

  goToDetail() {
      if (this.stateService.params.redirect) {
        this.stateService.go(this.stateService.params.redirect, { parcel: this.stateService.params.parcel }  );
      } else {
        this.stateService.go('^', { tab: this.titleId || this.constructionObjectId ? 'parcels' : 'occupations'  });
      }
  }

  async update() {
    this.occupationObject.priorityIndex = this.priorityHolder.priorityIndex ? this.priorityHolder.priorityIndex.id : 0;
    this.occupationObject.editMode = this.getApplicableEditModeOptions().length > 0 ? this.editMode : null;

    const finalSolutionTypeChanged = (this.occupationObject.finalSolutionType ? this.occupationObject.finalSolutionType.id : null) !== this.originalFinalSolutionTypeId;
    const reasonChanged = (this.occupationObject.finalSolutionReason || null) !== this.originalFinalSolutionReason;
    if (finalSolutionTypeChanged || reasonChanged) {
      this.occupationObject.finalSolutionState = FinalSolutionStateEnum.PENDING;
    }
    if (!this.occupationObject.finalSolutionType || this.occupationObject.finalSolutionType.key != FinalSolutionTypesEnum.OTHER_CHANGE) {
      delete this.occupationObject.finalSolutionOtherChange;
    } else {
      const finalSolutionOtherChange = this.occupationObject.finalSolutionOtherChange;
      if (finalSolutionOtherChange) {
        if (finalSolutionOtherChange.newConstructionObjects && finalSolutionOtherChange.newConstructionObjects.length) {
          finalSolutionOtherChange.newConstructionObjects = finalSolutionOtherChange.newConstructionObjects.map(co => ({ id: co.id }));
        }

        if (finalSolutionOtherChange.newOccupationType) {
          const {id, number, name} = finalSolutionOtherChange.newOccupationType;
          finalSolutionOtherChange.newOccupationType = ({id, number, name});
        }

        const shouldHaveNabyvatel = finalSolutionOtherChange.changeOccupationType &&
          finalSolutionOtherChange.newOccupationType &&
          PERMANENT_WITH_PURCHASE.includes(finalSolutionOtherChange.newOccupationType.number);

        if (shouldHaveNabyvatel && finalSolutionOtherChange.newNabyvatel) {
          finalSolutionOtherChange.newNabyvatel = {
            nabyvatel: finalSolutionOtherChange.newNabyvatel.id,
            acquirerId: finalSolutionOtherChange.newNabyvatel.acquirerId,
            typNabyvatele: finalSolutionOtherChange.newNabyvatel.typNabyvatele,
            icoNabyvatele: finalSolutionOtherChange.newNabyvatel.icoNabyvatele
          }
        }

        // delete unnecessary items
        !shouldHaveNabyvatel &&
          delete finalSolutionOtherChange.newNabyvatel;
        !finalSolutionOtherChange.changeGeometry &&
          delete finalSolutionOtherChange.changeGeometry;
        !finalSolutionOtherChange.changeWholeParcel &&
          delete finalSolutionOtherChange.changeWholeParcel;
        !finalSolutionOtherChange.changeOccupationType &&
          delete finalSolutionOtherChange.changeOccupationType &&
          delete finalSolutionOtherChange.newOccupationType;
        !finalSolutionOtherChange.changeConstructionObjects &&
          delete finalSolutionOtherChange.changeConstructionObjects &&
          delete finalSolutionOtherChange.newConstructionObjects;
        !finalSolutionOtherChange.changeOccupationNumber &&
          delete finalSolutionOtherChange.changeOccupationNumber &&
          delete finalSolutionOtherChange.newOccupationNumber;
      }
      this.occupationObject.finalSolutionState = FinalSolutionStateEnum.PENDING;
    }
    await this.restangular.one('occupations', this.occupationId).customPUT(this.occupationObject).toPromise();
    this.goToDetail();
  }

  onChangeExpert(expert: ExpertModel) {
    this.occupationObject.rentalExpert = expert;
  }

  isSolutionType(): boolean {
    return ((
      this.occupationObject.zabtyp == 3 || this.occupationObject.zabtyp == 7 ||
      this.occupationObject.zabtyp == 12 || this.occupationObject.zabtyp == 121 ||
      this.occupationObject.zabtyp == 122) && !this.brandService.isVisible('mpv'))
  }

  isChangeFinalSolutionType(): boolean {
    const finalSolutionType = this.occupationObject.finalSolutionType;
    if (finalSolutionType && finalSolutionType.key === FinalSolutionTypesEnum.OTHER_CHANGE) {
      const { finalSolutionOtherChange } = this.occupationObject;

      if (!finalSolutionOtherChange) {
        return false;
      }

      const changeGeometry = finalSolutionOtherChange.changeGeometry;
      const changeWholeParcel = finalSolutionOtherChange.changeWholeParcel;
      const changeOccupationType = finalSolutionOtherChange.changeOccupationType;
      const changeConstructionObjects = finalSolutionOtherChange.changeConstructionObjects;
      const changeOccupationNumber = finalSolutionOtherChange.changeOccupationNumber;

      const newOccupationNumber = finalSolutionOtherChange.newOccupationNumber;
      const newOccupationType = finalSolutionOtherChange.newOccupationType;
      const newNabyvatel = finalSolutionOtherChange.newNabyvatel;
      const newConstructionObjects = finalSolutionOtherChange.newConstructionObjects;

      // check if is all valid
      if (!(changeGeometry || changeWholeParcel || changeOccupationType || changeConstructionObjects || changeOccupationNumber) ||
          (changeOccupationType && !newOccupationType) ||
          (changeOccupationType && newOccupationType && this.permanentWithPurchase.includes(newOccupationType.number) && !newNabyvatel && this.acquirerList.length > 0) ||
          (changeConstructionObjects && newConstructionObjects && newConstructionObjects.length === 0) ||
          (changeOccupationNumber && (!newOccupationNumber || newOccupationNumber === this.occupationObject.zabcis.toString()))
      ) {
        return false;
      }
    }

    return this.brandService.isVisible('mpv') &&
      (
        (this.authService.getActualProject().type ===  ProjectTypesEnum.ADDITIONAL && finalSolutionType && finalSolutionType.key !== FinalSolutionTypesEnum.STATE_EMH && this.occupationObject.occupationType.category === 'permanent') ||
        (this.authService.getActualProject().type ===  ProjectTypesEnum.INVEST && finalSolutionType && finalSolutionType.key && finalSolutionType.key !==  FinalSolutionTypesEnum.STATE_ZE)
      ) &&
      ((finalSolutionType.key !== FinalSolutionTypesEnum.CANCELLED && finalSolutionType.key !== FinalSolutionTypesEnum.CANCELLED_INVEST) || Boolean(this.occupationObject.finalSolutionReason));
  }

  isValid() {
    return (
      this.editMode === 'final-solution-type' && this.isChangeFinalSolutionType()) ||
      this.editMode === 'priority' ||
      this.editMode === 'solution-type' ||
      (this.editMode === 'price' &&
        (!this.occupationObject.solutionType && !this.occupationLongerThanOneYear() && !this.occupationShorterThanOneYear()) ||
        (this.occupationObject.rentalPriceType && this.occupationObject.rentalLength > 0 && (
          ((this.occupationLongerThanOneYear() || this.occupationObject.solutionType === 'permanentWithSolutionTypeOverOneYear') && this.occupationObject.rentalLength > 12) ||
          ((this.occupationShorterThanOneYear() || this.occupationObject.solutionType === 'permanentWithSolutionTypeUnderOneYear') && this.occupationObject.rentalLength <= 12)
        ) && (
        (this.occupationObject.rentalPriceType === 'F' && this.occupationObject.priceAssessmentYear && this.occupationObject.priceAssessmentLandPrice) ||
        (this.occupationObject.rentalPriceType === 'E' && this.occupationObject.rentalExpertOpinionNumber && this.occupationObject.rentalExpertOpinionDate && this.occupationObject.rentalExpert && this.occupationObject.priceAssessmentLandPrice) ||
        (this.occupationObject.rentalPriceType === 'O' && this.occupationObject.rentalOtherPriceSource && ((this.occupationObject.rentalPriceUnit === 'T' && this.occupationObject.totalRentPrice >= 0) || (this.occupationObject.rentalPriceUnit === 'A' && this.occupationObject.priceAssessmentLandPrice >= 0)))
        ))
      );
  }

  occupationLongerThanOneYear() {
    return this.occupationObject.occupationType.category === 'overOneYear';
  }

  occupationShorterThanOneYear() {
    return this.occupationObject.occupationType.category === 'underOneYear';
  }

  onOccupationSolutionTypeOptionSelected(option: OccupationSolutionTypeModel) {
    this.occupationObject.finalSolutionReason = null;
    this.occupationObject.finalSolutionType = option;
  }
}

